import { useState, useEffect } from "react";

/**
 * Custom React hook to manage and format numeric input values.
 *
 * @param {string|number} [initialValue=""] - The initial value for the input, can be a string or number.
 * @param {string} [type="number"] - The type of value to format (currently only "number" is supported).
 * @returns {{
 *   rawValue: string,
 *   formattedValue: string,
 *   handleChange: (e: React.ChangeEvent<HTMLInputElement> | string) => string,
 *   setRawValue: React.Dispatch<React.SetStateAction<string>>
 * }} An object containing the raw value, formatted value, a change handler, and a setter for the raw value.
 *
 * @example
 * const { rawValue, formattedValue, handleChange } = useFormattedNumber("1000");
 * // rawValue: "1000"
 * // formattedValue: "1,000"
 */
export function useFormattedNumber(initialValue = "", type = "number") {
  const [rawValue, setRawValue] = useState(initialValue);
  const [formattedValue, setFormattedValue] = useState(
    formatNumber(initialValue)
  );

  // Sync if initialValue changes (e.g. from context update)
  useEffect(() => {
    setRawValue(initialValue);
    setFormattedValue(formatNumber(initialValue));
  }, [initialValue]);

  /**
   * Handles input changes by extracting the numeric value from the input,
   * updating the raw and formatted values accordingly.
   *
   * @param {React.ChangeEvent<HTMLInputElement> | string} e - The input event or a string value.
   * @returns {string} The raw numeric value extracted from the input.
   */
  const handleChange = (e) => {
    const input = typeof e === "string" ? e : e?.target?.value ?? "";
    const raw = input.replace(/[^\d.]/g, "");
    setRawValue(raw);
    setFormattedValue(formatNumber(raw));
    return raw;
  };
  

  return {
    rawValue,
    formattedValue,
    handleChange,
    setRawValue,
  };
}

/**
 * Formats a given value as a localized string with thousands separators and up to two decimal places.
 *
 * @param {number|string|null|undefined} value - The value to format. Can be a number, string, null, or undefined.
 * @returns {string} The formatted number as a string, or an empty string if the input is invalid.
 */
function formatNumber(val) {
  if (val === "" || val === null || val === undefined) return "";
  const n = Number(String(val).replace(/,/g, ""));
  if (!isFinite(n)) return "";
  return n.toLocaleString("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};
